/*
 * cx-names-art.js -- shared drawing core for the "Your Name on Your ..."
 * customised hard-surface products (luggage, helmet, tiffin box, school
 * bottle). Owned ONLY by those four products (file prefix cx-names-);
 * no other theme file loads it.
 *
 * Exposes window.CXNamesArt:
 *   ICONS            -- { key: { label, w, h, build(): Path2D, rule } }
 *   compose(opts)    -- Promise<{ canvas, capRatio }>: the flattened,
 *                       transparent, tightly-trimmed PNG source that is
 *                       uploaded as the "Custom Image" line-item property.
 *                       Operations OS prints it with its longest side equal
 *                       to the ordered size, so this canvas is the print.
 *   drawIcon(ctx, key, x, y, size, fillStyle)  -- icon-picker thumbnails.
 *
 * Icons are drawn as solid silhouettes (no hairlines, no detail smaller
 * than ~6% of the icon box) so they survive printing at ~0.4 inch tall.
 * Holes are wound anticlockwise against clockwise solids so the "nonzero"
 * fill rule unions overlapping parts and still punches eyes/windows out.
 */
(function () {
  'use strict';

  var TAU = Math.PI * 2;

  /* ---------------- path helpers (all coordinates in a w x h box) -------- */

  function circle(p, cx, cy, r, hole) {
    p.moveTo(cx + r, cy);
    p.arc(cx, cy, r, 0, TAU, !!hole);
    p.closePath();
  }

  function ellipse(p, cx, cy, rx, ry, rot, hole) {
    var c = Math.cos(rot || 0);
    var s = Math.sin(rot || 0);
    p.moveTo(cx + rx * c, cy + rx * s);
    p.ellipse(cx, cy, rx, ry, rot || 0, 0, TAU, !!hole);
    p.closePath();
  }

  function signedArea(pts) {
    var a = 0;
    for (var i = 0; i < pts.length; i++) {
      var j = (i + 1) % pts.length;
      a += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
    }
    return a;
  }

  // Polygon with forced winding: clockwise (on screen) for solids,
  // anticlockwise for holes -- regardless of the order points were listed.
  function poly(p, pts, hole) {
    var cw = signedArea(pts) > 0;
    var list = pts.slice();
    if (cw === !!hole) list.reverse();
    p.moveTo(list[0][0], list[0][1]);
    for (var i = 1; i < list.length; i++) p.lineTo(list[i][0], list[i][1]);
    p.closePath();
  }

  // Rounded rectangle, clockwise solid or anticlockwise hole.
  function rrect(p, x, y, w, h, r, hole) {
    r = Math.min(r, w / 2, h / 2);
    if (!hole) {
      p.moveTo(x + r, y);
      p.lineTo(x + w - r, y);
      p.arcTo(x + w, y, x + w, y + r, r);
      p.lineTo(x + w, y + h - r);
      p.arcTo(x + w, y + h, x + w - r, y + h, r);
      p.lineTo(x + r, y + h);
      p.arcTo(x, y + h, x, y + h - r, r);
      p.lineTo(x, y + r);
      p.arcTo(x, y, x + r, y, r);
    } else {
      p.moveTo(x + r, y);
      p.arcTo(x, y, x, y + r, r);
      p.lineTo(x, y + h - r);
      p.arcTo(x, y + h, x + r, y + h, r);
      p.lineTo(x + w - r, y + h);
      p.arcTo(x + w, y + h, x + w, y + h - r, r);
      p.lineTo(x + w, y + r);
      p.arcTo(x + w, y, x + w - r, y, r);
      p.lineTo(x + r, y);
    }
    p.closePath();
  }

  // A thick straight bar between two points with round ends (capsule).
  function bar(p, x1, y1, x2, y2, width) {
    var dx = x2 - x1;
    var dy = y2 - y1;
    var len = Math.sqrt(dx * dx + dy * dy) || 1;
    var nx = (-dy / len) * (width / 2);
    var ny = (dx / len) * (width / 2);
    var ang = Math.atan2(dy, dx);
    p.moveTo(x1 + nx, y1 + ny);
    p.arc(x1, y1, width / 2, ang + Math.PI / 2, ang + (3 * Math.PI) / 2, false);
    p.lineTo(x2 - nx, y2 - ny);
    p.arc(x2, y2, width / 2, ang - Math.PI / 2, ang + Math.PI / 2, false);
    p.closePath();
  }

  function star(p, cx, cy, rOuter, rInner, points, rotDeg, hole) {
    var pts = [];
    var rot = ((rotDeg || -90) * Math.PI) / 180;
    for (var i = 0; i < points * 2; i++) {
      var r = i % 2 === 0 ? rOuter : rInner;
      var a = rot + (i * Math.PI) / points;
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    poly(p, pts, hole);
  }

  // Rotates a list of [x,y] points around (cx,cy) by deg.
  function rot(pts, cx, cy, deg) {
    var a = (deg * Math.PI) / 180;
    var c = Math.cos(a);
    var s = Math.sin(a);
    return pts.map(function (pt) {
      var x = pt[0] - cx;
      var y = pt[1] - cy;
      return [cx + x * c - y * s, cy + x * s + y * c];
    });
  }

  function withMatrix(src, m) {
    var out = new Path2D();
    out.addPath(src, m);
    return out;
  }

  function rotated(src, cx, cy, deg) {
    var m = new DOMMatrix().translate(cx, cy).rotate(deg).translate(-cx, -cy);
    return withMatrix(src, m);
  }

  /* ---------------- icon library ---------------- */

  var ICONS = {
    /* ---- travel (luggage) ---- */
    plane: {
      label: 'Plane',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        bar(p, 50, 10, 50, 86, 13);
        poly(p, [[44, 38], [6, 60], [6, 69], [44, 57]]);
        poly(p, [[56, 38], [94, 60], [94, 69], [56, 57]]);
        poly(p, [[46, 74], [30, 85], [30, 92], [46, 87]]);
        poly(p, [[54, 74], [70, 85], [70, 92], [54, 87]]);
        return rotated(p, 50, 50, 45);
      },
    },
    globe: {
      label: 'Globe',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        circle(p, 50, 50, 46);
        circle(p, 50, 50, 37, true);
        ellipse(p, 50, 50, 21, 46);
        ellipse(p, 50, 50, 12, 37, 0, true);
        rrect(p, 6, 45.5, 88, 9, 2);
        rrect(p, 14, 23, 72, 7.5, 2);
        rrect(p, 14, 69.5, 72, 7.5, 2);
        return p;
      },
    },
    mountain: {
      label: 'Mountain',
      w: 100,
      h: 84,
      build: function () {
        var p = new Path2D();
        poly(p, [[2, 82], [40, 14], [66, 58], [74, 44], [98, 82]]);
        // snow cap cut-out on the big peak
        poly(p, [[29, 34], [40, 14], [51, 34], [46, 30], [40, 37], [34, 30]], true);
        circle(p, 82, 16, 10);
        return p;
      },
    },
    compass: {
      label: 'Compass',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        circle(p, 50, 50, 47);
        circle(p, 50, 50, 38, true);
        var needle = rot([[50, 16], [59, 50], [50, 84], [41, 50]], 50, 50, 40);
        poly(p, needle);
        circle(p, 50, 50, 5.5, true);
        // north tick
        poly(p, [[50, 3], [55, 11], [45, 11]]);
        return p;
      },
    },
    suitcase: {
      label: 'Suitcase',
      w: 100,
      h: 92,
      build: function () {
        var p = new Path2D();
        rrect(p, 30, 2, 40, 26, 9);
        rrect(p, 39, 10, 22, 18, 4, true);
        rrect(p, 6, 22, 88, 64, 10);
        rrect(p, 26, 32, 8, 44, 3, true);
        rrect(p, 66, 32, 8, 44, 3, true);
        circle(p, 22, 88, 4);
        circle(p, 78, 88, 4);
        return p;
      },
    },

    /* ---- helmet ---- */
    lightning: {
      label: 'Lightning',
      w: 70,
      h: 100,
      build: function () {
        var p = new Path2D();
        poly(p, [[44, 2], [6, 56], [30, 56], [20, 98], [64, 38], [40, 38], [56, 2]]);
        return p;
      },
    },
    flame: {
      label: 'Flame',
      w: 80,
      h: 100,
      build: function () {
        var p = new Path2D();
        p.addPath(
          new Path2D(
            'M40 2 C46 22 70 34 72 62 C74 84 58 98 40 98 C20 98 6 84 8 64 C9 50 18 42 22 30 C28 40 30 46 34 48 C34 34 36 18 40 2 Z'
          )
        );
        // inner flame cut-out (anticlockwise)
        p.addPath(new Path2D('M40 52 C34 62 26 70 28 80 C30 88 36 90 40 90 C48 90 54 84 52 74 C50 66 44 60 40 52 Z'));
        return p;
      },
      rule: 'evenodd',
    },
    star: {
      label: 'Star',
      w: 100,
      h: 96,
      build: function () {
        var p = new Path2D();
        star(p, 50, 52, 50, 21, 5, -90);
        return p;
      },
    },
    wings: {
      label: 'Wings',
      w: 120,
      h: 62,
      build: function () {
        // Two swept wings with a scalloped trailing edge and a small centre
        // badge. The three parts never overlap, so winding direction can't
        // cut accidental holes under the nonzero rule.
        var right =
          'M64 28 C80 12 100 4 119 0 C118 12 112 20 104 25 C109 28 111 31 110 35 C104 41 96 43 89 43 C92 46 92 50 90 53 C82 59 73 61 65 58 C63 48 62 38 64 28 Z';
        var p = new Path2D(right);
        p.addPath(new Path2D(right), new DOMMatrix([-1, 0, 0, 1, 120, 0]));
        circle(p, 60, 44, 6.5);
        return p;
      },
    },
    flag: {
      label: 'Racing flag',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        bar(p, 12, 6, 12, 96, 8);
        var cell = 14;
        for (var r = 0; r < 4; r++) {
          for (var c = 0; c < 5; c++) {
            if ((r + c) % 2 === 0) rrect(p, 17 + c * cell, 6 + r * cell, cell, cell, 1.5);
          }
        }
        // thin frame so the chequer reads as one flag, not loose squares
        rrect(p, 17, 6, 70, 56, 2);
        rrect(p, 21, 10, 62, 48, 1, true);
        return p;
      },
    },

    /* ---- kids ---- */
    dino: {
      label: 'Dinosaur',
      w: 110,
      h: 90,
      build: function () {
        var p = new Path2D();
        ellipse(p, 52, 58, 30, 19);
        // neck + head
        poly(p, [[66, 50], [74, 22], [88, 22], [82, 54]]);
        ellipse(p, 86, 18, 15, 10, -0.15);
        // tail
        poly(p, [[26, 52], [2, 66], [4, 72], [30, 68]]);
        // legs
        rrect(p, 30, 64, 11, 24, 5);
        rrect(p, 46, 66, 11, 22, 5);
        rrect(p, 62, 64, 11, 24, 5);
        // back plates
        poly(p, [[34, 42], [40, 30], [46, 40]]);
        poly(p, [[46, 40], [52, 28], [58, 39]]);
        // eye
        circle(p, 90, 15, 3.4, true);
        return p;
      },
    },
    unicorn: {
      label: 'Unicorn',
      w: 90,
      h: 100,
      build: function () {
        var p = new Path2D();
        // head + neck silhouette facing left
        p.addPath(
          new Path2D(
            // written clockwise so the anticlockwise eye/nostril holes cut out
            'M54 26 C64 28 70 36 74 46 C80 60 82 80 80 98 L38 98 C40 88 40 78 38 70 C32 70 26 74 18 76 C10 78 4 72 6 64 C8 58 14 50 20 42 C28 30 40 24 54 26 Z'
          )
        );
        // horn
        poly(p, [[44, 30], [58, 2], [54, 32]]);
        // ear
        poly(p, [[58, 30], [66, 14], [68, 34]]);
        // mane tufts down the back of the neck
        circle(p, 74, 40, 9);
        circle(p, 80, 54, 9);
        circle(p, 84, 68, 9);
        circle(p, 85, 82, 9);
        // eye + nostril holes
        circle(p, 40, 44, 4, true);
        circle(p, 13, 66, 2.8, true);
        return p;
      },
      rule: 'nonzero',
    },
    rocket: {
      label: 'Rocket',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        p.addPath(new Path2D('M50 4 C64 16 70 34 70 52 L70 70 L30 70 L30 52 C30 34 36 16 50 4 Z'));
        poly(p, [[30, 50], [14, 70], [16, 80], [30, 70]]);
        poly(p, [[70, 50], [86, 70], [84, 80], [70, 70]]);
        poly(p, [[40, 70], [60, 70], [56, 78], [44, 78]]);
        // exhaust flame
        p.addPath(new Path2D('M42 82 L58 82 C58 90 54 96 50 99 C46 96 42 90 42 82 Z'));
        circle(p, 50, 38, 8, true);
        return rotated(p, 50, 50, 35);
      },
    },
    car: {
      label: 'Car',
      w: 110,
      h: 76,
      build: function () {
        var p = new Path2D();
        p.addPath(new Path2D('M30 14 L72 14 C78 14 82 18 86 24 L94 36 L30 36 Z'));
        poly(p, [[22, 36], [30, 14], [36, 14], [36, 36]]);
        rrect(p, 4, 32, 102, 26, 10);
        // windows
        poly(p, [[36, 20], [54, 20], [54, 33], [32, 33]], true);
        poly(p, [[59, 20], [72, 20], [80, 33], [59, 33]], true);
        // wheels with hubs
        circle(p, 28, 60, 13);
        circle(p, 82, 60, 13);
        circle(p, 28, 60, 5, true);
        circle(p, 82, 60, 5, true);
        return p;
      },
    },
    butterfly: {
      label: 'Butterfly',
      w: 100,
      h: 90,
      build: function () {
        var p = new Path2D();
        ellipse(p, 30, 30, 22, 18, -0.5);
        ellipse(p, 70, 30, 22, 18, 0.5);
        ellipse(p, 32, 62, 16, 13, 0.5);
        ellipse(p, 68, 62, 16, 13, -0.5);
        circle(p, 28, 28, 6.5, true);
        circle(p, 72, 28, 6.5, true);
        rrect(p, 45, 22, 10, 56, 5);
        bar(p, 48, 22, 38, 6, 3.6);
        bar(p, 52, 22, 62, 6, 3.6);
        circle(p, 38, 6, 4);
        circle(p, 62, 6, 4);
        return p;
      },
    },
    cricket: {
      label: 'Cricket bat',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        var bat = new Path2D();
        rrect(bat, 38, 36, 24, 62, 8);
        rrect(bat, 46, 4, 8, 36, 3);
        rrect(bat, 44, 4, 12, 20, 4);
        p.addPath(rotated(bat, 50, 50, -32));
        circle(p, 26, 84, 11);
        return p;
      },
    },
    panda: {
      label: 'Panda',
      w: 100,
      h: 92,
      build: function () {
        var p = new Path2D();
        circle(p, 20, 20, 15);
        circle(p, 80, 20, 15);
        ellipse(p, 50, 52, 40, 36);
        ellipse(p, 34, 50, 9.5, 12.5, 0.45, true);
        ellipse(p, 66, 50, 9.5, 12.5, -0.45, true);
        circle(p, 35, 49, 4);
        circle(p, 65, 49, 4);
        ellipse(p, 50, 68, 7, 5, 0, true);
        return p;
      },
    },
    rainbow: {
      label: 'Rainbow',
      w: 110,
      h: 70,
      build: function () {
        var p = new Path2D();
        var cx = 55;
        var cy = 58;
        [[50, 40], [35, 26], [21, 13]].forEach(function (b) {
          p.moveTo(cx - b[0], cy);
          p.arc(cx, cy, b[0], Math.PI, TAU, false);
          p.lineTo(cx + b[1], cy);
          p.arc(cx, cy, b[1], TAU, Math.PI, true);
          p.closePath();
        });
        // clouds at both feet
        [[10, 60, 10], [22, 56, 12], [32, 62, 8], [78, 62, 8], [88, 56, 12], [100, 60, 10]].forEach(function (c) {
          circle(p, c[0], c[1], c[2]);
        });
        rrect(p, 2, 58, 36, 12, 6);
        rrect(p, 72, 58, 36, 12, 6);
        return p;
      },
    },
    football: {
      label: 'Football',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        circle(p, 50, 50, 47);
        star(p, 50, 50, 13, 13 * Math.cos(Math.PI / 5), 5, -90, true);
        for (var i = 0; i < 5; i++) {
          var a = ((-90 + 36 + i * 72) * Math.PI) / 180;
          var cx = 50 + Math.cos(a) * 34;
          var cy = 50 + Math.sin(a) * 34;
          var pts = [];
          for (var k = 0; k < 5; k++) {
            var b = a + Math.PI + (k * TAU) / 5;
            pts.push([cx + Math.cos(b) * 9, cy + Math.sin(b) * 9]);
          }
          poly(p, pts, true);
        }
        return p;
      },
    },
    cat: {
      label: 'Cat',
      w: 100,
      h: 90,
      build: function () {
        var p = new Path2D();
        poly(p, [[10, 44], [14, 2], [42, 26]]);
        poly(p, [[90, 44], [86, 2], [58, 26]]);
        ellipse(p, 50, 54, 42, 34);
        ellipse(p, 34, 50, 6, 8.5, 0, true);
        ellipse(p, 66, 50, 6, 8.5, 0, true);
        poly(p, [[44, 64], [56, 64], [50, 71]], true);
        return p;
      },
    },
    star_kids: {
      label: 'Star',
      w: 100,
      h: 96,
      build: function () {
        var p = new Path2D();
        star(p, 50, 52, 50, 25, 5, -90);
        return p;
      },
    },
    heart: {
      label: 'Heart',
      w: 100,
      h: 90,
      build: function () {
        return new Path2D(
          'M50 88 C34 74 4 56 4 30 C4 14 16 2 30 2 C40 2 46 8 50 16 C54 8 60 2 70 2 C84 2 96 14 96 30 C96 56 66 74 50 88 Z'
        );
      },
    },

    /* ---- home (main door) ---- */
    diya: {
      label: 'Diya',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        // flame
        p.addPath(new Path2D('M50 4 C60 18 66 30 60 42 C57 48 43 48 40 42 C34 30 40 18 50 4 Z'));
        // bowl with a pointed spout on the right
        p.addPath(new Path2D('M6 60 L94 60 C92 70 88 76 80 82 C70 90 30 90 20 82 C12 76 8 70 6 60 Z M86 60 L98 52 L94 64 Z'));
        rrect(p, 34, 88, 32, 8, 4);
        return p;
      },
    },
    flower: {
      label: 'Marigold',
      w: 100,
      h: 100,
      build: function () {
        var p = new Path2D();
        for (var i = 0; i < 8; i++) {
          var a = (i * TAU) / 8;
          ellipse(p, 50 + Math.cos(a) * 30, 50 + Math.sin(a) * 30, 16, 12, a);
        }
        circle(p, 50, 50, 22);
        circle(p, 50, 50, 10, true);
        return p;
      },
    },
    home: {
      label: 'Home',
      w: 100,
      h: 96,
      build: function () {
        var p = new Path2D();
        poly(p, [[50, 4], [96, 46], [84, 46], [84, 94], [16, 94], [16, 46], [4, 46]]);
        rrect(p, 40, 62, 20, 32, 4, true);
        return p;
      },
    },
  };

  var pathCache = {};
  function iconPath(key) {
    if (!ICONS[key]) return null;
    if (!pathCache[key]) pathCache[key] = ICONS[key].build();
    return pathCache[key];
  }

  /* ---------------- fills ---------------- */

  // fill: { type: 'solid', color } or { type: 'gradient', stops: [c1, c2, ...] }
  function makeFill(ctx, fill, x0, y0, x1, y1) {
    if (!fill || fill.type !== 'gradient') return (fill && fill.color) || '#111111';
    var g = ctx.createLinearGradient(x0, y0, x1, y1);
    var n = fill.stops.length;
    fill.stops.forEach(function (c, i) {
      g.addColorStop(n === 1 ? 0 : i / (n - 1), c);
    });
    return g;
  }

  function drawIcon(ctx, key, x, y, size, fillStyle) {
    var icon = ICONS[key];
    var path = iconPath(key);
    if (!icon || !path) return;
    var scale = size / Math.max(icon.w, icon.h);
    ctx.save();
    ctx.translate(x + (size - icon.w * scale) / 2, y + (size - icon.h * scale) / 2);
    ctx.scale(scale, scale);
    ctx.fillStyle = fillStyle;
    ctx.fill(path, icon.rule || 'nonzero');
    ctx.restore();
  }

  /* ---------------- trimming ---------------- */

  function trim(canvas) {
    var ctx = canvas.getContext('2d');
    var w = canvas.width;
    var h = canvas.height;
    var data = ctx.getImageData(0, 0, w, h).data;
    var top = -1;
    var bottom = -1;
    var left = w;
    var right = -1;
    for (var y = 0; y < h; y++) {
      var row = y * w * 4;
      for (var x = 0; x < w; x++) {
        if (data[row + x * 4 + 3] > 8) {
          if (top < 0) top = y;
          bottom = y;
          if (x < left) left = x;
          if (x > right) right = x;
        }
      }
    }
    if (top < 0) return canvas;
    var out = document.createElement('canvas');
    out.width = right - left + 1;
    out.height = bottom - top + 1;
    out.getContext('2d').drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height);
    return out;
  }

  /* ---------------- compose ---------------- */

  function loadFont(family, text) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    return document.fonts.load('200px "' + family + '"', text || 'Ag').catch(function () {});
  }

  /*
   * opts: {
   *   text, fontFamily,
   *   fill: { type:'solid', color } | { type:'gradient', stops:[...] },
   *   outline: null | '#ffffff' | '#111111',
   *   icon: null | key,
   *   maxSide: target longest side in px (2400 for print, smaller for preview)
   * }
   */
  function compose(opts) {
    var text = (opts.text || '').trim();
    return loadFont(opts.fontFamily, text).then(function () {
      var F = 200;
      var fontStr = F + 'px "' + opts.fontFamily + '"';
      var mctx = document.createElement('canvas').getContext('2d');
      mctx.font = fontStr;

      var m = mctx.measureText(text || ' ');
      var capM = mctx.measureText('H');
      var cap = capM.actualBoundingBoxAscent || F * 0.7;
      var tLeft = -(m.actualBoundingBoxLeft || 0);
      var tRight = m.actualBoundingBoxRight || m.width;
      var tTop = -(m.actualBoundingBoxAscent || cap);
      var tBottom = m.actualBoundingBoxDescent || 0;
      if (!text) {
        tLeft = 0;
        tRight = 0;
        tTop = 0;
        tBottom = 0;
      }

      var outlineW = opts.outline ? Math.max(6, cap * 0.13) : 0;
      var hasIcon = !!(opts.icon && ICONS[opts.icon]);
      var icon = hasIcon ? ICONS[opts.icon] : null;
      var iconBox = 0;
      var iconW = 0;
      var iconH = 0;
      var gap = 0;
      if (hasIcon) {
        iconBox = cap * 1.55;
        var s = iconBox / Math.max(icon.w, icon.h);
        iconW = icon.w * s;
        iconH = icon.h * s;
        gap = text ? cap * 0.32 + outlineW * 0.6 : 0;
      }

      // Layout in "F" units: icon to the left of the text, both centred on
      // the cap-height midline so the icon sits with the letters, not with
      // descenders.
      var textX = hasIcon ? iconW + gap - tLeft : -tLeft;
      var baseline = 0;
      var iconX = 0;
      var iconY = baseline - cap / 2 - iconH / 2;

      var minX = Math.min(hasIcon ? iconX : Infinity, textX + tLeft);
      var maxX = Math.max(hasIcon ? iconX + iconW : -Infinity, textX + tRight);
      var minY = Math.min(hasIcon ? iconY : Infinity, baseline + tTop);
      var maxY = Math.max(hasIcon ? iconY + iconH : -Infinity, baseline + tBottom);
      if (!isFinite(minX)) {
        minX = 0;
        maxX = 1;
        minY = 0;
        maxY = 1;
      }

      var pad = outlineW + 4;
      var boxW = maxX - minX + pad * 2;
      var boxH = maxY - minY + pad * 2;
      var maxSide = opts.maxSide || 2400;
      var scale = maxSide / Math.max(boxW, boxH);

      var canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.ceil(boxW * scale));
      canvas.height = Math.max(1, Math.ceil(boxH * scale));
      var ctx = canvas.getContext('2d');
      ctx.setTransform(scale, 0, 0, scale, (pad - minX) * scale, (pad - minY) * scale);
      ctx.font = fontStr;
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';

      var fillStyle = makeFill(ctx, opts.fill, minX, 0, maxX, 0);
      var iconScale = hasIcon ? iconW / icon.w : 1;
      var path = hasIcon ? iconPath(opts.icon) : null;

      if (opts.outline) {
        ctx.save();
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.miterLimit = 2;
        ctx.strokeStyle = opts.outline;
        ctx.fillStyle = opts.outline;
        ctx.lineWidth = outlineW * 2;
        if (text) {
          ctx.strokeText(text, textX, baseline);
          ctx.fillText(text, textX, baseline);
        }
        if (hasIcon) {
          ctx.save();
          ctx.translate(iconX, iconY);
          ctx.scale(iconScale, iconScale);
          ctx.lineWidth = (outlineW * 2) / iconScale;
          ctx.stroke(path);
          ctx.fill(path, 'nonzero');
          ctx.restore();
        }
        ctx.restore();
      }

      ctx.fillStyle = fillStyle;
      if (text) ctx.fillText(text, textX, baseline);
      if (hasIcon) {
        ctx.save();
        ctx.translate(iconX, iconY);
        ctx.scale(iconScale, iconScale);
        // Gradient coordinates live in the outer (layout) space -- undo the
        // icon's own transform for the fill so one gradient spans icon+text.
        if (opts.fill && opts.fill.type === 'gradient') {
          ctx.fillStyle = makeFill(ctx, opts.fill, (minX - iconX) / iconScale, 0, (maxX - iconX) / iconScale, 0);
        }
        ctx.fill(path, icon.rule || 'nonzero');
        ctx.restore();
      }

      var trimmed = trim(canvas);
      var longest = Math.max(trimmed.width, trimmed.height) || 1;
      return {
        canvas: trimmed,
        // Cap height of the lettering as a fraction of the printed longest
        // side -- used to warn when a long name at a small size would print
        // with letters too small to read comfortably.
        capRatio: text ? (cap * scale) / longest : 0,
      };
    });
  }


  /* ---------------- object mockups ----------------
   * Stylised flat drawings in a 1000x1000 box. Colours come from CSS custom
   * properties set on the stage element (--cx-body / --cx-shade /
   * --cx-light) so the customer can preview their patch on a different
   * coloured object. unitsPerInch maps the chosen patch size to mockup
   * units, so the preview shows real relative size (same idea as the
   * laptop product's 12-inch lid calibration).
   */
  var MOCKUPS = {
    luggage: {
      label: 'suitcase',
      unitsPerInch: 50, // body is 700 units = a 14 inch wide cabin suitcase
      anchor: [500, 440],
      maxWidth: 640,
      swatches: [
        ['Navy', '#1f2d5a'],
        ['Silver', '#c3c9d1'],
        ['Red', '#c62b35'],
        ['Black', '#26282d'],
        ['Teal', '#16807f'],
      ],
      svg:
        '<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<ellipse cx="500" cy="956" rx="340" ry="20" fill="rgba(0,0,0,.14)"/>' +
        '<rect x="392" y="92" width="22" height="130" rx="6" fill="#a3a9b1"/>' +
        '<rect x="586" y="92" width="22" height="130" rx="6" fill="#a3a9b1"/>' +
        '<rect x="366" y="58" width="268" height="52" rx="26" fill="#2f3237"/>' +
        '<rect x="214" y="876" width="72" height="44" rx="12" fill="#2f3237"/>' +
        '<rect x="714" y="876" width="72" height="44" rx="12" fill="#2f3237"/>' +
        '<circle cx="250" cy="930" r="25" fill="#1b1d20"/>' +
        '<circle cx="750" cy="930" r="25" fill="#1b1d20"/>' +
        '<rect x="356" y="192" width="288" height="30" rx="10" fill="var(--cx-shade)"/>' +
        '<rect x="150" y="210" width="700" height="690" rx="72" fill="var(--cx-body)"/>' +
        '<rect x="174" y="234" width="652" height="642" rx="54" fill="none" stroke="var(--cx-shade)" stroke-width="7"/>' +
        '<g fill="var(--cx-shade)">' +
        '<rect x="262" y="650" width="18" height="196" rx="9"/><rect x="372" y="650" width="18" height="196" rx="9"/>' +
        '<rect x="491" y="650" width="18" height="196" rx="9"/><rect x="610" y="650" width="18" height="196" rx="9"/>' +
        '<rect x="720" y="650" width="18" height="196" rx="9"/></g>' +
        '<rect x="196" y="262" width="56" height="360" rx="28" fill="#ffffff" opacity=".12"/>' +
        '</svg>',
    },
    helmet: {
      label: 'helmet',
      unitsPerInch: 59, // shell is ~710 units = a 12 inch long helmet
      anchor: [722, 566],
      maxWidth: 380,
      swatches: [
        ['Matte black', '#26282d'],
        ['White', '#eceef1'],
        ['Red', '#c8202f'],
        ['Yellow', '#f2c230'],
        ['Grey', '#7a808a'],
      ],
      svg:
        '<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<ellipse cx="540" cy="872" rx="380" ry="22" fill="rgba(0,0,0,.14)"/>' +
        '<path d="M190 640 C170 400 330 190 560 180 C780 172 905 330 905 540 L905 690 C905 770 852 822 772 822 L330 822 C250 822 200 762 190 690 Z" fill="var(--cx-body)"/>' +
        '<path d="M196 470 C220 380 300 330 420 330 C470 330 500 350 510 390 L520 520 C522 560 500 590 460 594 L232 600 C206 600 190 580 192 560 Z" fill="#20242b"/>' +
        '<path d="M244 424 C286 384 344 368 414 370" stroke="#ffffff" stroke-opacity=".35" stroke-width="18" fill="none" stroke-linecap="round"/>' +
        '<circle cx="540" cy="470" r="18" fill="var(--cx-shade)"/>' +
        '<path d="M232 780 L884 780 C900 780 906 792 902 804 C892 818 862 824 822 824 L322 824 C272 824 244 806 232 780 Z" fill="#2a2d33"/>' +
        '<rect x="214" y="644" width="74" height="16" rx="8" fill="var(--cx-shade)"/>' +
        '<rect x="214" y="676" width="74" height="16" rx="8" fill="var(--cx-shade)"/>' +
        '<rect x="560" y="200" width="96" height="18" rx="9" fill="var(--cx-shade)" transform="rotate(6 608 209)"/>' +
        '<path d="M612 236 C730 238 822 312 856 424" stroke="#ffffff" stroke-opacity=".16" stroke-width="30" fill="none" stroke-linecap="round"/>' +
        '</svg>',
    },
    tiffin: {
      label: 'tiffin box',
      unitsPerInch: 92.5, // lid is 740 units = an 8 inch school lunch box
      anchor: [500, 360],
      maxWidth: 560,
      swatches: [
        ['Yellow', '#ffd54a'],
        ['Sky blue', '#5bb8f0'],
        ['Pink', '#ff8fb8'],
        ['Green', '#6ccb5f'],
        ['Purple', '#9c7be8'],
      ],
      // Open lunch box: lid hinged up and facing the viewer (the patch sits
      // on it), the food compartments visible in front.
      svg:
        '<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<ellipse cx="500" cy="912" rx="410" ry="22" fill="rgba(0,0,0,.13)"/>' +
        // lid (upright)
        '<rect x="130" y="140" width="740" height="450" rx="56" fill="var(--cx-shade)"/>' +
        '<rect x="130" y="128" width="740" height="446" rx="56" fill="var(--cx-body)"/>' +
        '<rect x="168" y="162" width="664" height="378" rx="40" fill="none" stroke="var(--cx-light)" stroke-width="10" opacity=".7"/>' +
        '<rect x="196" y="186" width="260" height="20" rx="10" fill="#ffffff" opacity=".35"/>' +
        // hinges
        '<rect x="260" y="566" width="90" height="30" rx="10" fill="var(--cx-shade)"/>' +
        '<rect x="650" y="566" width="90" height="30" rx="10" fill="var(--cx-shade)"/>' +
        // box rim + interior (seen from slightly above)
        '<path d="M110 600 L890 600 L870 770 L130 770 Z" fill="var(--cx-body)"/>' +
        '<path d="M148 618 L852 618 L836 752 L164 752 Z" fill="#fbf7ef"/>' +
        '<path d="M470 618 L478 752" stroke="var(--cx-light)" stroke-width="12"/>' +
        '<path d="M474 684 L846 684" stroke="var(--cx-light)" stroke-width="12"/>' +
        // rotis (left compartment)
        '<ellipse cx="270" cy="700" rx="96" ry="40" fill="#d9a35b"/><ellipse cx="270" cy="694" rx="96" ry="40" fill="#e8bb72"/>' +
        '<ellipse cx="350" cy="690" rx="96" ry="40" fill="#d9a35b"/><ellipse cx="350" cy="684" rx="96" ry="40" fill="#efc77f"/>' +
        '<g fill="#b9802f" opacity=".55"><circle cx="330" cy="676" r="7"/><circle cx="372" cy="692" r="6"/><circle cx="352" cy="668" r="5"/><circle cx="250" cy="702" r="6"/></g>' +
        // sabzi (top right)
        '<g fill="#e09a2c"><ellipse cx="560" cy="652" rx="58" ry="20"/><ellipse cx="660" cy="648" rx="64" ry="22"/><ellipse cx="760" cy="652" rx="56" ry="20"/></g>' +
        '<g fill="#6aa83b"><circle cx="540" cy="646" r="12"/><circle cx="604" cy="654" r="10"/><circle cx="690" cy="642" r="12"/><circle cx="744" cy="656" r="10"/><circle cx="790" cy="646" r="9"/></g>' +
        // fruit (bottom right): apple slices + grapes
        '<g><path d="M520 736 Q560 690 604 736 Z" fill="#fff3d6" stroke="#d93a3a" stroke-width="8"/>' +
        '<path d="M600 738 Q640 692 684 738 Z" fill="#fff3d6" stroke="#d93a3a" stroke-width="8"/>' +
        '<g fill="#8fc24a"><circle cx="730" cy="722" r="16"/><circle cx="762" cy="716" r="16"/><circle cx="794" cy="724" r="16"/><circle cx="746" cy="740" r="15"/><circle cx="780" cy="742" r="15"/></g></g>' +
        // front face of the box
        '<path d="M130 770 L870 770 L858 872 Q854 900 824 900 L176 900 Q146 900 142 872 Z" fill="var(--cx-body)"/>' +
        '<path d="M138 846 L862 846 L858 872 Q854 900 824 900 L176 900 Q146 900 142 872 Z" fill="var(--cx-shade)" opacity=".5"/>' +
        // open flip clips hanging on the front
        '<rect x="196" y="766" width="78" height="96" rx="18" fill="#f6f6f6" stroke="rgba(0,0,0,.13)" stroke-width="5"/>' +
        '<rect x="726" y="766" width="78" height="96" rx="18" fill="#f6f6f6" stroke="rgba(0,0,0,.13)" stroke-width="5"/>' +
        '</svg>',
    },
    bottle: {
      label: 'bottle',
      unitsPerInch: 120, // body is 360 units = a 3 inch wide school bottle
      anchor: [500, 600],
      maxWidth: 340,
      swatches: [
        ['Sky blue', '#5bb8f0'],
        ['Pink', '#ff8fb8'],
        ['Green', '#6ccb5f'],
        ['Purple', '#9c7be8'],
        ['Yellow', '#ffd54a'],
      ],
      svg:
        '<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<ellipse cx="500" cy="954" rx="210" ry="18" fill="rgba(0,0,0,.14)"/>' +
        '<path d="M630 196 C724 190 748 292 686 330" stroke="#2f3237" stroke-width="22" fill="none" stroke-linecap="round"/>' +
        '<rect x="456" y="84" width="88" height="96" rx="26" fill="#f4f4f4" stroke="rgba(0,0,0,.14)" stroke-width="5"/>' +
        '<rect x="360" y="148" width="280" height="160" rx="52" fill="#f4f4f4" stroke="rgba(0,0,0,.14)" stroke-width="5"/>' +
        '<rect x="372" y="292" width="256" height="44" rx="12" fill="var(--cx-shade)"/>' +
        '<rect x="320" y="318" width="360" height="620" rx="84" fill="var(--cx-body)"/>' +
        '<rect x="320" y="842" width="360" height="20" fill="var(--cx-shade)" opacity=".7"/>' +
        '<rect x="320" y="874" width="360" height="20" fill="var(--cx-shade)" opacity=".7"/>' +
        '<rect x="626" y="372" width="30" height="430" rx="15" fill="#ffffff" opacity=".22"/>' +
        '</svg>',
    },
    tank: {
      label: 'bike tank',
      unitsPerInch: 42, // tank side is ~700 units = a 17 inch tank
      anchor: [500, 450],
      maxWidth: 520,
      swatches: [
        ['Black', '#1e2024'],
        ['Red', '#c8202f'],
        ['Blue', '#1f4fa8'],
        ['Grey', '#7a808a'],
        ['White', '#eceef1'],
        ['Mint', '#9fd8c4'],
      ],
      // Side view of the front half of a motorcycle: fork and headlight on
      // the left, the fuel tank (patch area) in the middle, seat nose on the
      // right and the engine below.
      svg:
        '<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<ellipse cx="520" cy="930" rx="440" ry="20" fill="rgba(0,0,0,.12)"/>' +
        '<path d="M150 250 L70 920" stroke="#a9afb6" stroke-width="34" stroke-linecap="round"/>' +
        '<path d="M178 250 L98 920" stroke="#80868e" stroke-width="10" stroke-linecap="round"/>' +
        '<path d="M120 170 L250 150" stroke="#2b2c30" stroke-width="22" stroke-linecap="round"/>' +
        '<circle cx="110" cy="340" r="72" fill="#2b2c30"/><circle cx="110" cy="340" r="54" fill="#dfe3e8"/><circle cx="96" cy="326" r="18" fill="#ffffff" opacity=".8"/>' +
        '<rect x="330" y="640" width="400" height="230" rx="24" fill="#3a3d42"/>' +
        '<g stroke="#55595f" stroke-width="10"><path d="M350 690 L710 690"/><path d="M350 730 L710 730"/><path d="M350 770 L710 770"/><path d="M350 810 L710 810"/></g>' +
        '<path d="M200 600 L900 600" stroke="#2b2c30" stroke-width="30" stroke-linecap="round"/>' +
        '<path d="M800 380 C880 360 960 370 1000 400 L1000 520 C930 520 860 510 810 500 Z" fill="#1d1e21"/>' +
        '<path d="M190 470 C180 370 260 290 400 270 C540 250 700 258 810 300 C850 316 860 350 852 400 L836 540 C828 600 780 630 700 636 L330 640 C250 640 198 580 190 470 Z" fill="var(--cx-body)"/>' +
        '<path d="M330 640 C250 638 206 600 196 540 C340 572 600 580 840 540 C830 600 784 630 700 636 Z" fill="var(--cx-shade)" opacity=".55"/>' +
        '<path d="M250 340 C360 296 560 286 760 312" stroke="#ffffff" stroke-opacity=".3" stroke-width="20" fill="none" stroke-linecap="round"/>' +
        '<rect x="450" y="240" width="130" height="30" rx="13" fill="#9aa0a8"/>' +
        '<rect x="470" y="226" width="90" height="20" rx="9" fill="#c4c9cf"/>' +
        '<path d="M690 470 C740 460 800 470 836 490 L828 560 C800 590 740 600 700 600 Z" fill="var(--cx-shade)" opacity=".35"/>' +
        '</svg>',
    },
    door: {
      label: 'main door',
      unitsPerInch: 36, // the door slab is ~880 units = 24 inches of a door in close-up
      anchor: [500, 470],
      maxWidth: 640,
      swatches: [
        ['Teak', '#6b3f22'],
        ['Walnut', '#3e2a1e'],
        ['Oak', '#c49a6c'],
        ['White', '#efeae2'],
        ['Maroon', '#6e1f2a'],
        ['Grey', '#6f757c'],
      ],
      // Close-up of the upper half of an Indian main door with a toran.
      svg:
        '<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<rect x="0" y="0" width="1000" height="1000" fill="#efe6d8"/>' +
        '<rect x="40" y="40" width="920" height="960" fill="#d9ccb8"/>' +
        '<rect x="60" y="60" width="880" height="940" fill="var(--cx-body)"/>' +
        '<rect x="120" y="230" width="760" height="460" rx="6" fill="none" stroke="var(--cx-shade)" stroke-width="14"/>' +
        '<rect x="134" y="244" width="732" height="432" rx="4" fill="none" stroke="var(--cx-light)" stroke-width="4" opacity=".5"/>' +
        '<rect x="120" y="760" width="350" height="240" rx="6" fill="none" stroke="var(--cx-shade)" stroke-width="14"/>' +
        '<rect x="530" y="760" width="350" height="240" rx="6" fill="none" stroke="var(--cx-shade)" stroke-width="14"/>' +
        '<rect x="820" y="820" width="26" height="120" rx="13" fill="#c9a227"/>' +
        '<path d="M40 70 Q500 190 960 70" stroke="#3f7d2c" stroke-width="10" fill="none"/>' +
        '<g fill="#4f9a35">' +
        '<path d="M150 104 q14 40 0 70 q-14 -30 0 -70z"/><path d="M280 138 q14 40 0 70 q-14 -30 0 -70z"/>' +
        '<path d="M420 156 q14 40 0 70 q-14 -30 0 -70z"/><path d="M580 156 q14 40 0 70 q-14 -30 0 -70z"/>' +
        '<path d="M720 138 q14 40 0 70 q-14 -30 0 -70z"/><path d="M850 104 q14 40 0 70 q-14 -30 0 -70z"/></g>' +
        '<g fill="#f59a1b"><circle cx="90" cy="86" r="22"/><circle cx="215" cy="124" r="22"/><circle cx="350" cy="150" r="22"/>' +
        '<circle cx="500" cy="160" r="24"/><circle cx="650" cy="150" r="22"/><circle cx="785" cy="124" r="22"/><circle cx="910" cy="86" r="22"/></g>' +
        '<g fill="#ffc433"><circle cx="90" cy="86" r="10"/><circle cx="215" cy="124" r="10"/><circle cx="350" cy="150" r="10"/>' +
        '<circle cx="500" cy="160" r="11"/><circle cx="650" cy="150" r="10"/><circle cx="785" cy="124" r="10"/><circle cx="910" cy="86" r="10"/></g>' +
        '</svg>',
    },
  };

  function mixHex(hex, other, t) {
    var a = parseInt(hex.slice(1), 16);
    var b = parseInt(other.slice(1), 16);
    var r = Math.round(((a >> 16) & 255) * (1 - t) + ((b >> 16) & 255) * t);
    var g = Math.round(((a >> 8) & 255) * (1 - t) + ((b >> 8) & 255) * t);
    var bl = Math.round((a & 255) * (1 - t) + (b & 255) * t);
    return '#' + ((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1);
  }

  function applyObjectColour(el, hex) {
    el.style.setProperty('--cx-body', hex);
    el.style.setProperty('--cx-shade', mixHex(hex, '#000000', 0.22));
    el.style.setProperty('--cx-light', mixHex(hex, '#ffffff', 0.35));
  }

  function luminance(hex) {
    var n = parseInt(hex.slice(1), 16);
    var c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function contrast(hexA, hexB) {
    var a = luminance(hexA);
    var b = luminance(hexB);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }

  /* ---------------- colour palettes ---------------- */

  var SOLIDS = [
    ['Black', '#111111'],
    ['White', '#ffffff'],
    ['Red', '#e32636'],
    ['Orange', '#ff7a1a'],
    ['Yellow', '#ffd21f'],
    ['Lime', '#7ed957'],
    ['Green', '#1e9e4a'],
    ['Teal', '#12a4a0'],
    ['Sky Blue', '#38b6ff'],
    ['Royal Blue', '#1f4fd8'],
    ['Navy', '#1b2a5b'],
    ['Purple', '#7b3fe4'],
    ['Pink', '#ff5fa2'],
    ['Baby Pink', '#ffb3cf'],
    ['Gold', '#c9a227'],
    ['Silver', '#a7afb8'],
  ];

  var GRADIENTS = [
    ['Sunset', ['#ff5f6d', '#ffc371']],
    ['Ocean', ['#00c6fb', '#005bea']],
    ['Rainbow', ['#ff3b3b', '#ff9f1a', '#ffe11a', '#3ecf4c', '#2d8cff', '#8a4dff']],
    ['Neon', ['#ff2bd6', '#7b2bff', '#00e5ff']],
    ['Fire', ['#ffd200', '#ff6a00', '#e5002b']],
    ['Candy', ['#ff9ac1', '#b18cff', '#7fd6ff']],
  ];


  // Where the composed patch sits on a mockup, as percentages of the
  // (square) stage. The patch's LONGEST side equals the chosen size, which
  // is exactly how Operations OS sizes the print.
  function patchBox(objectKey, sizeIn, imgW, imgH) {
    var m = MOCKUPS[objectKey];
    var longest = sizeIn * m.unitsPerInch;
    var w = imgW >= imgH ? longest : (longest * imgW) / imgH;
    var h = imgW >= imgH ? (longest * imgH) / imgW : longest;
    var clamped = false;
    if (w > m.maxWidth) {
      h = (h * m.maxWidth) / w;
      w = m.maxWidth;
      clamped = true;
    }
    return {
      left: (m.anchor[0] - w / 2) / 10,
      top: (m.anchor[1] - h / 2) / 10,
      width: w / 10,
      height: h / 10,
      clamped: clamped,
    };
  }

  window.CXNamesArt = {
    ICONS: ICONS,
    compose: compose,
    drawIcon: drawIcon,
    loadFont: loadFont,
    makeFill: makeFill,
    MOCKUPS: MOCKUPS,
    SOLIDS: SOLIDS,
    GRADIENTS: GRADIENTS,
    applyObjectColour: applyObjectColour,
    contrast: contrast,
    mixHex: mixHex,
    patchBox: patchBox,
  };
})();
